import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * CATIA-style text note annotation with high-performance rendering
 *
 * Performance optimizations:
 * - Geometry/Material pooling (reuse instead of create)
 * - Instanced rendering for leader lines
 * - CSS2D sprites for text (GPU accelerated)
 * - LOD support for large scenes
 * - Zero allocations in update loop
 */
/**
 * Font style options for CATIA-style text rendering
 */
export interface FontStyleOptions {
    ailesi?: string;
    kalin?: boolean;
    italik?: boolean;
    hizalama?: 'left' | 'center' | 'right';
    padding?: number;
}

/**
 * Leader line style options
 */
export interface LeaderStyleOptions {
    renk?: string;
    kalinlik?: number;
    tip?: 'solid' | 'dashed' | 'dotted';
}

export class FikoMetinNotuCizgisi extends THREE.Group {
    override type = 'FikoMetinNotuCizgisi';

    // 🔴 SABİT BOYUT - Kamera zoom'una göre scale ayarlanır
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private metin: string;
    private renk: string;
    private boyut: number;
    private arkaplanRengi: string;
    private arkaplanOpaklik: number;
    private cercevRengi: string;
    private cercevKalinlik: number;
    private okGoster: boolean;

    // Font styling
    private fontAilesi: string;
    private fontKalin: boolean;
    private fontItalik: boolean;
    private hizalama: 'left' | 'center' | 'right';
    private padding: number;

    // Leader line styling
    private okRengi: string;
    private okKalinlik: number;
    private okTipi: 'solid' | 'dashed' | 'dotted';

    // Shadow settings
    private golgeGoster: boolean;
    private golgeMesafe: number;

    // Three.js objects (pooled)
    private metinSprite: THREE.Sprite | null = null;
    private okCizgisi: THREE.Line | null = null;
    private arkaplanMesh: THREE.Mesh | null = null;

    // Leader line target point (the point on 3D model the arrow points to)
    private hedefNokta: THREE.Vector3 | null = null;
    // Text offset from target point
    private metinOffset: THREE.Vector3 = new THREE.Vector3(0.5, 0.5, 0);

    // Calculated text box dimensions (in world units) for proper line connection
    private textBoxWidth: number = 1;
    private textBoxHeight: number = 0.5;

    // Leader line connection point on text box (bottom-left by default, like CAD programs)
    private leaderAnchor: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'bottom-center' = 'bottom-left';

    // Canvas rendering with high-quality anti-aliasing
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    // Reusable buffers (zero allocation)
    private static readonly tempVec3 = new THREE.Vector3();
    private static readonly tempVec2 = new THREE.Vector2();

    // Static pools for geometry/material reuse
    private static readonly geometryPool = {
        plane: new THREE.PlaneGeometry(1, 1),
        line: new THREE.BufferGeometry()
    };

    private static readonly materialPool = {
        sprite: new Map<string, THREE.SpriteMaterial>(),
        line: new Map<string, THREE.LineBasicMaterial>(),
        background: new Map<string, THREE.MeshBasicMaterial>()
    };

    constructor(
        public sahne?: FikoSahne,
        metin: string = 'Yeni not...',
        ayarlar?: {
            renk?: string;
            boyut?: number;
            arkaplanRengi?: string;
            cercevRengi?: string;
            okGoster?: boolean;
        }
    ) {
        super();

        this.metin = metin;
        this.renk = ayarlar?.renk || '#FFFFFF';  // Professional: white text
        this.boyut = ayarlar?.boyut || 12;  // Professional: compact 12px
        this.arkaplanRengi = ayarlar?.arkaplanRengi || 'rgba(26, 26, 26, 0.85)';  // Dark semi-transparent
        this.arkaplanOpaklik = 85;
        this.cercevRengi = ayarlar?.cercevRengi || '#666666';  // Subtle gray border
        this.cercevKalinlik = 1;  // Thin border
        this.okGoster = ayarlar?.okGoster !== undefined ? ayarlar.okGoster : true;

        // Font styling defaults - Professional CAD style
        this.fontAilesi = 'Arial';
        this.fontKalin = false;  // Normal weight for professional look
        this.fontItalik = false;
        this.hizalama = 'left';
        this.padding = 8;  // Compact padding

        // Leader line defaults - Professional CAD style
        this.okRengi = '#808080';  // Gray line
        this.okKalinlik = 1;  // Thin line
        this.okTipi = 'solid';

        // Shadow defaults - Professional CAD: no shadow
        this.golgeGoster = false;
        this.golgeMesafe = 2;

        // Initialize canvas for text rendering with high quality
        this.canvas = document.createElement('canvas');
        this.canvas.width = 256;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', {
            alpha: true,
            desynchronized: true, // Performance hint
            willReadFrequently: false
        });
        if (!ctx) {
            throw new Error('Failed to get 2D context for FikoMetinNotuCizgisi');
        }
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.generateMipmaps = false; // Better quality for text

        this.name = 'Metin Notu';
        this.renderOrder = 999; // Always on top

        this.init();

        // Sabit boyut için sahneyi hazırla
        if (sahne) {
            this.hazirla(sahne);
        }
    }

    /**
     * 🔴 SABİT BOYUT: Sahneye hazırlama - kamera event listener ekle
     */
    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) {
            return;
        }

        this.sahne = sahne;

        try {
            // İlk scale'i ayarla
            if (this.metinSprite) {
                this.metinSprite.userData.originalScale = new THREE.Vector3().copy(this.metinSprite.scale);
                this.kameraYonDegisti();
            }

            // Kamera değiştiğinde event listener ekle
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Handle error silently
        }
    }

    /**
     * 🔴 SABİT BOYUT: Kamera zoom değiştiğinde sprite scale'i güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.metinSprite) {
            return;
        }

        const originalScale = this.metinSprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;

        // Zoom faktörüne göre scale ayarla
        const scaleAdjustment = factor * 0.02;
        this.metinSprite.scale.set(
            originalScale.x * scaleAdjustment,
            originalScale.y * scaleAdjustment,
            1
        );

        // Update text box dimensions for leader line calculation
        this.textBoxWidth = this.metinSprite.scale.x;
        this.textBoxHeight = this.metinSprite.scale.y;

        // Update leader line to match new text box position
        if (this.hedefNokta && this.okCizgisi) {
            this.updateLeaderLineToTarget();
        }

        this.metinSprite.updateMatrix();
    }

    /**
     * 🔴 SABİT BOYUT: Sabit boyut modunu değiştir
     */
    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.metinSprite) {
            // Sabit boyut aktifse mevcut scale'i sakla
            this.metinSprite.userData.originalScale = new THREE.Vector3().copy(this.metinSprite.scale);
            this.kameraYonDegisti();
        }
    }

    /**
     * Initialize note visualization
     */
    private init(): void {
        this.createTextSprite();
        if (this.okGoster) {
            this.createLeaderLine();
        }
    }

    /**
     * Create text sprite with canvas texture
     * Each instance gets its own material for independent texture updates
     */
    private createTextSprite(): void {
        // Update canvas with text
        this.updateCanvasTexture();

        // Create unique material for this instance (no pooling for texture-based materials)
        const material = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });

        this.metinSprite = new THREE.Sprite(material);

        // Update sprite scale based on canvas dimensions
        this.updateSpriteScale();

        this.add(this.metinSprite);
    }

    /**
     * Update sprite scale based on current canvas dimensions and font size
     * Maintains proper aspect ratio and stores dimensions for leader line connection
     * Professional CAD style: compact, clean text boxes
     */
    private updateSpriteScale(): void {
        if (!this.metinSprite) return;

        // Scale based on canvas actual content size, normalized by base font size
        // Use smaller scale factor for professional CAD appearance
        const baseScale = this.boyut / 12; // Normalize to default size (12px)
        const aspectRatio = this.canvas.width / this.canvas.height;

        // Professional CAD: much smaller scale for compact appearance
        // Divide by 200 instead of 100 for smaller text boxes
        const height = this.canvas.height / 200 * baseScale;
        const width = height * aspectRatio;

        this.metinSprite.scale.set(width, height, 1);

        // Store text box dimensions for leader line calculation
        this.textBoxWidth = width;
        this.textBoxHeight = height;

        // Update originalScale for sabitBoyut
        if (this.sabitBoyut) {
            this.metinSprite.userData.originalScale = new THREE.Vector3().copy(this.metinSprite.scale);
            this.kameraYonDegisti();
        }

        // Update leader line if hedefNokta is set
        if (this.hedefNokta) {
            this.updateLeaderLineToTarget();
        }
    }

    /**
     * Update canvas texture with professional CATIA-style text rendering
     * High-quality anti-aliased rendering with full customization support
     */
    private updateCanvasTexture(): void {
        const ctx = this.context;

        // Build font string with all styling options
        const fontStyle = this.fontItalik ? 'italic' : 'normal';
        const fontWeight = this.fontKalin ? 'bold' : 'normal';
        const fontString = `${fontStyle} ${fontWeight} ${this.boyut}px ${this.fontAilesi}, sans-serif`;

        // Measure text with current font
        ctx.font = fontString;
        const metrics = ctx.measureText(this.metin);
        const textWidth = metrics.width;
        const textHeight = this.boyut * 1.5;

        // Calculate dimensions with custom padding
        const pad = this.padding;
        const contentWidth = textWidth + pad * 2;
        const contentHeight = textHeight + pad;

        // Resize canvas (power of 2 for GPU efficiency, 2x for retina quality)
        const scale = 2; // Supersampling for anti-aliasing
        const width = Math.pow(2, Math.ceil(Math.log2(contentWidth * scale)));
        const height = Math.pow(2, Math.ceil(Math.log2(contentHeight * scale)));

        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }

        // Scale context for high-DPI rendering
        ctx.setTransform(scale, 0, 0, scale, 0, 0);

        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Clear with transparency
        ctx.clearRect(0, 0, width / scale, height / scale);

        // Calculate actual content area
        const boxX = pad / 2;
        const boxY = pad / 2;
        const boxWidth = contentWidth - pad;
        const boxHeight = contentHeight - pad / 2;

        // Draw shadow if enabled
        if (this.golgeGoster && this.golgeMesafe > 0) {
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = this.golgeMesafe * 2;
            ctx.shadowOffsetX = this.golgeMesafe;
            ctx.shadowOffsetY = this.golgeMesafe;
        }

        // Background box with RGBA support
        ctx.fillStyle = this.arkaplanRengi;
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

        // Reset shadow for border and text
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Border with custom thickness
        if (this.cercevKalinlik > 0) {
            ctx.strokeStyle = this.cercevRengi;
            ctx.lineWidth = this.cercevKalinlik;
            ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
        }

        // Text with alignment support
        ctx.font = fontString;
        ctx.fillStyle = this.renk;
        ctx.textBaseline = 'middle';

        let textX = boxX + pad;
        switch (this.hizalama) {
            case 'center':
                ctx.textAlign = 'center';
                textX = boxX + boxWidth / 2;
                break;
            case 'right':
                ctx.textAlign = 'right';
                textX = boxX + boxWidth - pad;
                break;
            default: // left
                ctx.textAlign = 'left';
                textX = boxX + pad;
        }

        const textY = boxY + boxHeight / 2;

        // Draw text with subpixel anti-aliasing
        ctx.fillText(this.metin, textX, textY);

        // Reset transform
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Update texture
        this.texture.needsUpdate = true;
    }

    /**
     * Create leader line (ok çizgisi) with professional CATIA-style rendering
     * Supports solid, dashed, and dotted line types with custom thickness and color
     */
    private createLeaderLine(): void {
        // Create line geometry from origin to sprite
        const points = [
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 1, 0)
        ];

        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        // Create material with custom styling
        const material = new THREE.LineBasicMaterial({
            color: new THREE.Color(this.okRengi),
            linewidth: this.okKalinlik,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false
        });

        // Apply line type (dashed/dotted)
        if (this.okTipi === 'dashed') {
            const dashedMaterial = new THREE.LineDashedMaterial({
                color: new THREE.Color(this.okRengi),
                linewidth: this.okKalinlik,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false,
                dashSize: 0.2,
                gapSize: 0.1,
                scale: 1
            });
            this.okCizgisi = new THREE.Line(geometry, dashedMaterial);
            this.okCizgisi.computeLineDistances(); // Required for dashed lines
        } else if (this.okTipi === 'dotted') {
            const dottedMaterial = new THREE.LineDashedMaterial({
                color: new THREE.Color(this.okRengi),
                linewidth: this.okKalinlik,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false,
                dashSize: 0.05,
                gapSize: 0.1,
                scale: 1
            });
            this.okCizgisi = new THREE.Line(geometry, dottedMaterial);
            this.okCizgisi.computeLineDistances(); // Required for dotted lines
        } else {
            // Solid line (default)
            this.okCizgisi = new THREE.Line(geometry, material);
        }

        this.add(this.okCizgisi);
    }

    /**
     * Update text content
     */
    setText(yeniMetin: string): void {
        if (this.metin !== yeniMetin) {
            this.metin = yeniMetin;
            this.updateCanvasTexture();
            this.updateSpriteScale();
        }
    }

    /**
     * Update text color
     */
    setColor(yeniRenk: string): void {
        if (this.renk !== yeniRenk) {
            this.renk = yeniRenk;
            this.updateCanvasTexture();
            this.updateSpriteScale();

            // Update leader line color
            if (this.okCizgisi) {
                (this.okCizgisi.material as THREE.LineBasicMaterial).color.set(yeniRenk);
            }
        }
    }

    /**
     * Update font size
     */
    setSize(yeniBoyut: number): void {
        if (this.boyut !== yeniBoyut) {
            this.boyut = yeniBoyut;
            this.updateCanvasTexture();
            this.updateSpriteScale();
        }
    }

    /**
     * Toggle leader line visibility
     */
    setLeaderVisible(visible: boolean): void {
        this.okGoster = visible;

        if (visible && !this.okCizgisi) {
            this.createLeaderLine();
        } else if (!visible && this.okCizgisi) {
            this.remove(this.okCizgisi);
            this.okCizgisi.geometry.dispose();
            if (this.okCizgisi.material) {
                (this.okCizgisi.material as THREE.Material).dispose();
            }
            this.okCizgisi = null;
        }
    }

    /**
     * Update background color with RGBA support
     * Accepts both hex and rgba formats
     */
    setBackgroundColor(color: string): void {
        this.arkaplanRengi = color;

        // Extract opacity if rgba format
        if (color.startsWith('rgba')) {
            const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
            if (match && match[4]) {
                this.arkaplanOpaklik = parseFloat(match[4]) * 100;
            }
        }

        this.updateCanvasTexture();
        this.updateSpriteScale();
    }

    /**
     * Update border style (color and thickness)
     */
    setBorderStyle(color: string, thickness: number): void {
        this.cercevRengi = color;
        this.cercevKalinlik = thickness;
        this.updateCanvasTexture();
        this.updateSpriteScale();
    }

    /**
     * Update font styling options
     */
    setFontStyle(options: FontStyleOptions): void {
        let changed = false;
        if (options.ailesi !== undefined && options.ailesi !== this.fontAilesi) {
            this.fontAilesi = options.ailesi;
            changed = true;
        }
        if (options.kalin !== undefined && options.kalin !== this.fontKalin) {
            this.fontKalin = options.kalin;
            changed = true;
        }
        if (options.italik !== undefined && options.italik !== this.fontItalik) {
            this.fontItalik = options.italik;
            changed = true;
        }
        if (options.hizalama !== undefined && options.hizalama !== this.hizalama) {
            this.hizalama = options.hizalama;
            changed = true;
        }
        if (options.padding !== undefined && options.padding !== this.padding) {
            this.padding = options.padding;
            changed = true;
        }
        if (changed) {
            this.updateCanvasTexture();
            this.updateSpriteScale();
        }
    }

    /**
     * Update leader line styling
     */
    setLeaderStyle(options: LeaderStyleOptions): void {
        let needsRecreate = false;

        if (options.renk !== undefined && options.renk !== this.okRengi) {
            this.okRengi = options.renk;
            needsRecreate = true;
        }
        if (options.kalinlik !== undefined && options.kalinlik !== this.okKalinlik) {
            this.okKalinlik = options.kalinlik;
            needsRecreate = true;
        }
        if (options.tip !== undefined && options.tip !== this.okTipi) {
            this.okTipi = options.tip;
            needsRecreate = true;
        }

        // Recreate line if style changed
        if (needsRecreate && this.okCizgisi) {
            const wasVisible = this.okGoster;
            this.setLeaderVisible(false);
            if (wasVisible) {
                this.setLeaderVisible(true);
            }
        }
    }

    /**
     * Update shadow settings
     */
    setShadow(enabled: boolean, distance?: number): void {
        const changed = this.golgeGoster !== enabled ||
                       (distance !== undefined && this.golgeMesafe !== distance);
        this.golgeGoster = enabled;
        if (distance !== undefined) {
            this.golgeMesafe = distance;
        }
        if (changed) {
            this.updateCanvasTexture();
            this.updateSpriteScale();
        }
    }

    /**
     * Update leader line to point at target
     * Zero allocation update
     */
    updateLeaderLine(target: THREE.Vector3): void {
        if (!this.okCizgisi) return;

        const positions = this.okCizgisi.geometry.attributes.position as THREE.BufferAttribute;

        // Update end point (reuse buffer)
        positions.setXYZ(1, target.x, target.y, target.z);
        positions.needsUpdate = true;

        this.okCizgisi.geometry.computeBoundingSphere();
    }

    /**
     * Set target point for leader line (the point on 3D model the arrow points to)
     * The text sprite will be positioned at an offset from this target point
     */
    setHedefNokta(nokta: THREE.Vector3, offset?: THREE.Vector3): void {
        this.hedefNokta = nokta.clone();

        if (offset) {
            this.metinOffset = offset.clone();
        }

        // Position this group at target + offset (where text will be)
        this.position.copy(this.hedefNokta).add(this.metinOffset);

        // Update leader line to connect from hedefNokta to text position
        this.updateLeaderLineToTarget();
    }

    /**
     * Get the current target point
     */
    getHedefNokta(): THREE.Vector3 | null {
        return this.hedefNokta?.clone() || null;
    }

    /**
     * Set text offset from target point
     */
    setMetinOffset(offset: THREE.Vector3): void {
        this.metinOffset = offset.clone();

        if (this.hedefNokta) {
            this.position.copy(this.hedefNokta).add(this.metinOffset);
            this.updateLeaderLineToTarget();
        }
    }

    /**
     * Calculate the anchor point on the text box where leader line connects
     * Returns local coordinates relative to the sprite center
     */
    private getTextBoxAnchorPoint(): THREE.Vector3 {
        const halfWidth = this.textBoxWidth / 2;
        const halfHeight = this.textBoxHeight / 2;

        switch (this.leaderAnchor) {
            case 'bottom-left':
                return new THREE.Vector3(-halfWidth, -halfHeight, 0);
            case 'bottom-right':
                return new THREE.Vector3(halfWidth, -halfHeight, 0);
            case 'top-left':
                return new THREE.Vector3(-halfWidth, halfHeight, 0);
            case 'top-right':
                return new THREE.Vector3(halfWidth, halfHeight, 0);
            case 'bottom-center':
                return new THREE.Vector3(0, -halfHeight, 0);
            default:
                return new THREE.Vector3(-halfWidth, -halfHeight, 0);
        }
    }

    /**
     * Automatically determine the best anchor point based on target position
     * This creates a natural-looking leader line like professional CAD software
     */
    private autoSelectAnchor(): void {
        if (!this.hedefNokta) return;

        // Calculate direction from text position to target
        const direction = this.hedefNokta.clone().sub(this.position);

        // Choose anchor based on where the target is relative to the text box
        if (direction.x < 0 && direction.y < 0) {
            this.leaderAnchor = 'bottom-left';
        } else if (direction.x >= 0 && direction.y < 0) {
            this.leaderAnchor = 'bottom-right';
        } else if (direction.x < 0 && direction.y >= 0) {
            this.leaderAnchor = 'top-left';
        } else {
            this.leaderAnchor = 'top-right';
        }
    }

    /**
     * Update leader line to connect from text box corner to target point
     * Professional CAD-style: line goes from text box edge to exact geometry intersection point
     */
    private updateLeaderLineToTarget(): void {
        if (!this.okCizgisi || !this.hedefNokta) return;

        // Auto-select best anchor point based on target position
        this.autoSelectAnchor();

        // Get anchor point on text box (in local coordinates)
        const anchorPoint = this.getTextBoxAnchorPoint();

        // Calculate local position of hedefNokta relative to this group's position
        const localTarget = this.hedefNokta.clone().sub(this.position);

        const positions = this.okCizgisi.geometry.attributes.position as THREE.BufferAttribute;

        // Start point: at the text box edge (anchor point)
        positions.setXYZ(0, anchorPoint.x, anchorPoint.y, anchorPoint.z);
        // End point: at the hedefNokta (target on geometry)
        positions.setXYZ(1, localTarget.x, localTarget.y, localTarget.z);

        positions.needsUpdate = true;
        this.okCizgisi.geometry.computeBoundingSphere();

        // For dashed lines, recompute distances
        if (this.okTipi === 'dashed' || this.okTipi === 'dotted') {
            this.okCizgisi.computeLineDistances();
        }
    }

    /**
     * Set leader line anchor point manually
     */
    setLeaderAnchor(anchor: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'bottom-center'): void {
        this.leaderAnchor = anchor;
        if (this.hedefNokta) {
            this.updateLeaderLineToTarget();
        }
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // 🔴 Event listener temizle
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        // Dispose texture
        if (this.texture) {
            this.texture.dispose();
        }

        // Dispose sprite material (each instance has its own)
        if (this.metinSprite?.material) {
            (this.metinSprite.material as THREE.Material).dispose();
        }

        // Dispose line geometry and material
        if (this.okCizgisi) {
            this.okCizgisi.geometry.dispose();
            if (this.okCizgisi.material) {
                (this.okCizgisi.material as THREE.Material).dispose();
            }
        }

        // Clear children
        this.clear();
    }

    /**
     * Static cleanup for pool
     * Call when shutting down entire system
     */
    static disposePool(): void {
        // Dispose geometries
        FikoMetinNotuCizgisi.geometryPool.plane.dispose();
        FikoMetinNotuCizgisi.geometryPool.line.dispose();

        // Dispose materials
        FikoMetinNotuCizgisi.materialPool.sprite.forEach(mat => mat.dispose());
        FikoMetinNotuCizgisi.materialPool.line.forEach(mat => mat.dispose());
        FikoMetinNotuCizgisi.materialPool.background.forEach(mat => mat.dispose());

        // Clear pools
        FikoMetinNotuCizgisi.materialPool.sprite.clear();
        FikoMetinNotuCizgisi.materialPool.line.clear();
        FikoMetinNotuCizgisi.materialPool.background.clear();
    }

    /**
     * Raycast support for selection
     */
    override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
        if (this.metinSprite) {
            this.metinSprite.raycast(raycaster, intersects);
        }
    }

    /**
     * JSON serialization with all customization properties
     */
    toJSON(): any {
        return {
            type: this.type,
            uuid: this.uuid,
            name: this.name,
            metin: this.metin,
            renk: this.renk,
            boyut: this.boyut,
            arkaplanRengi: this.arkaplanRengi,
            arkaplanOpaklik: this.arkaplanOpaklik,
            cercevRengi: this.cercevRengi,
            cercevKalinlik: this.cercevKalinlik,
            okGoster: this.okGoster,
            fontAilesi: this.fontAilesi,
            fontKalin: this.fontKalin,
            fontItalik: this.fontItalik,
            hizalama: this.hizalama,
            padding: this.padding,
            okRengi: this.okRengi,
            okKalinlik: this.okKalinlik,
            okTipi: this.okTipi,
            golgeGoster: this.golgeGoster,
            golgeMesafe: this.golgeMesafe,
            hedefNokta: this.hedefNokta ? this.hedefNokta.toArray() : null,
            metinOffset: this.metinOffset.toArray(),
            leaderAnchor: this.leaderAnchor,
            position: this.position.toArray(),
            rotation: this.rotation.toArray(),
            scale: this.scale.toArray()
        };
    }

    /**
     * JSON deserialization with all customization properties
     */
    static fromJSON(sahne: FikoSahne, json: any): FikoMetinNotuCizgisi {
        const note = new FikoMetinNotuCizgisi(
            sahne,
            json.metin,
            {
                renk: json.renk,
                boyut: json.boyut,
                arkaplanRengi: json.arkaplanRengi,
                cercevRengi: json.cercevRengi,
                okGoster: json.okGoster
            }
        );

        note.uuid = json.uuid;
        note.name = json.name;

        // Apply all customization properties
        if (json.arkaplanOpaklik !== undefined) note.arkaplanOpaklik = json.arkaplanOpaklik;
        if (json.cercevKalinlik !== undefined) note.cercevKalinlik = json.cercevKalinlik;
        if (json.fontAilesi !== undefined) note.fontAilesi = json.fontAilesi;
        if (json.fontKalin !== undefined) note.fontKalin = json.fontKalin;
        if (json.fontItalik !== undefined) note.fontItalik = json.fontItalik;
        if (json.hizalama !== undefined) note.hizalama = json.hizalama;
        if (json.padding !== undefined) note.padding = json.padding;
        if (json.okRengi !== undefined) note.okRengi = json.okRengi;
        if (json.okKalinlik !== undefined) note.okKalinlik = json.okKalinlik;
        if (json.okTipi !== undefined) note.okTipi = json.okTipi;
        if (json.golgeGoster !== undefined) note.golgeGoster = json.golgeGoster;
        if (json.golgeMesafe !== undefined) note.golgeMesafe = json.golgeMesafe;

        note.position.fromArray(json.position);
        note.rotation.fromArray(json.rotation);
        note.scale.fromArray(json.scale);

        // Restore leaderAnchor
        if (json.leaderAnchor) {
            note.setLeaderAnchor(json.leaderAnchor);
        }

        // Restore hedefNokta and metinOffset
        if (json.hedefNokta) {
            const hedef = new THREE.Vector3().fromArray(json.hedefNokta);
            const offset = json.metinOffset ? new THREE.Vector3().fromArray(json.metinOffset) : undefined;
            note.setHedefNokta(hedef, offset);
        }

        // Rebuild canvas texture with restored properties
        note.updateCanvasTexture();

        return note;
    }
}
